import { ProfileStoreService } from './../../mobile-system/services/profile-store-service';
import { Component, ElementRef, HostListener, Inject, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { IMarkerItem } from 'app/system/mobile-models/IMarkerItem';
import { ICoordinatesViewModel } from 'app/system/mobile-models/profile-models';
import { IPointItem, IPolygonItem, IPolylineItem } from 'app/system/mobile-system/object-history-common.models';
import { Feature, View } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { LineString, Point, Polygon } from 'ol/geom';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { fromLonLat, toLonLat } from 'ol/proj';
import { OSM } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { Circle as CircleStyle, Text } from 'ol/style';
import { DOCUMENT } from '@angular/common';


@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css'],
  standalone: false
})
export class MapComponent implements OnDestroy, OnInit, OnChanges {

  map: Map;
  vectorSource = new VectorSource();
  vectorLayer = new VectorLayer({ source: this.vectorSource });

  @Input() center: [number, number] = null;
  @Input() zoom: number = 2;
  @Input() markers: IMarkerItem[] = [];
  @Input() polygons: IPolygonItem[] = [];
  @Input() polylines: IPolylineItem[] = [];
  @Input() pointItems: IPointItem[] = [];

  private routeAllSource = new VectorSource();
  private routeDoneSource = new VectorSource();
  private markerSource = new VectorSource();
  private parkingSource = new VectorSource();
  private fuelingSource = new VectorSource();
  private drainingSource = new VectorSource();
  private profileStoreService = inject(ProfileStoreService);

  private routeAllLayer = new VectorLayer({ source: this.routeAllSource });
  private routeDoneLayer = new VectorLayer({ source: this.routeDoneSource });
  private markerLayer = new VectorLayer({ source: this.markerSource });
  private parkingLayer = new VectorLayer({ source: this.parkingSource });
  private fuelingLayer = new VectorLayer({ source: this.fuelingSource });
  private drainingLayer = new VectorLayer({ source: this.drainingSource });

  private routeAllProj: Coordinate[] = [];   // full route (EPSG:3857)
  private markerFeature: Feature<Point> | null = null;
  private parkingFeatures: Feature<Point>[] | null = [];
  private fuelingFeatures: Feature<Point>[] | null = [];
  private drainingFeatures: Feature<Point>[] | null = [];


  constructor(private elementRef: ElementRef, @Inject(DOCUMENT) private document: Document) { }

  ngOnInit() {
    const center = this.profileStoreService.readMapCenter();
    const zoom = this.profileStoreService.readMapZoom();
    this.map = new Map({
      view: new View({
        center: fromLonLat([center.latitude, center.longitude]),
        zoom: zoom,
      }),
      layers: [
        new TileLayer({ source: new OSM() }),
        this.vectorLayer,
        this.routeAllLayer,
        this.routeDoneLayer,
        this.markerLayer,
        this.parkingLayer,
        this.fuelingLayer,
        this.drainingLayer,
      ],
      target: 'ol-map'
    });
    this.map.setTarget(this.elementRef.nativeElement);

    // styles
    this.routeAllLayer.setStyle(new Style({
      stroke: new Stroke({ color: '#5c5fe2', width: 5, lineCap: 'round', lineJoin: 'round' })
    }));

    this.routeDoneLayer.setStyle(new Style({
      stroke: new Stroke({ color: '#00b5b8', width: 4, lineCap: 'round', lineJoin: 'round' }) // teal
    }));

    this.parkingLayer.setStyle(new Style({
      image: new Icon({ src: "assets/icons/mobile-map/ico_park_blue.svg", anchor: [0.5, 0.5], scale: 0.25 })
    }));

    this.fuelingLayer.setStyle(new Style({
      image: new Icon({ src: "assets/icons/monitoring/ico_fueling_blue.svg", anchor: [0.5, 0.5], scale: 0.25 })
    }));

    this.drainingLayer.setStyle(new Style({
      image: new Icon({ src: "assets/icons/monitoring/ico_draining-red.svg", anchor: [0.5, 0.5], scale: 0.25 })
    }));

    this.rebuildVectorContent();
  }

  ngOnDestroy(): void {
    this.saveView();
  }

  /** Optional: also save on browser tab close / refresh */
  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.saveView();
  }

  @HostListener('window:pagehide', ['$event'])
  onPageHide(e: PageTransitionEvent) {
    this.saveView();
  }

  // Fires when app/tab is backgrounded (very important on mobile)
  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (this.document.visibilityState === 'hidden') {
      this.saveView();
    }
  }

  /** Central save logic */
  private saveView(): void {
    if (!this.map) return;

    const center = toLonLat(this.map.getView().getCenter())
    const zoom = this.map.getView().getZoom();

    this.profileStoreService.saveMapCenter({ latitude: center[0], longitude: center[1] });
    this.profileStoreService.saveMapZoom(zoom);
  }

  public setRoute = (lonLat: Array<[number, number]>): void => {
    this.routeAllProj = (lonLat ?? []).map(([lon, lat]) => fromLonLat([lon, lat]));
    // set "full route" geometry (gray)
    this.routeAllSource.clear();
    if (this.routeAllProj.length >= 2) {
      const full = new Feature(new LineString(this.routeAllProj));
      this.routeAllSource.addFeature(full);
    }

    // reset "done" geometry (teal)
    this.routeDoneSource.clear();

    // position marker at the start (optional)
    // if (this.routeAllProj.length) {
    //   const [x, y] = this.routeAllProj[0];
    //   this.ensureMarker();
    //   this.markerFeature!.getGeometry()!.setCoordinates([x, y]);
    // }
  };

  public mapClear = (): void => {
    this.routeAllSource.clear();
    this.parkingSource.removeFeatures(this.parkingFeatures);
    this.parkingFeatures = [];
    this.parkingSource.clear();

    this.fuelingSource.removeFeatures(this.fuelingFeatures);
    this.fuelingFeatures = [];
    this.fuelingSource.clear();


    this.drainingSource.removeFeatures(this.drainingFeatures);
    this.drainingFeatures = [];
    this.drainingSource.clear();

    this.vectorSource.clear();
    this.routeDoneSource.clear();
    this.markerSource.removeFeature(this.markerFeature);
    this.markerFeature = null;
  }

  /** Fit viewport to the full route (with padding) */
  public fitRoute = (): void => {
    if (!this.routeAllProj.length) return;
    const extent = new LineString(this.routeAllProj).getExtent();
    this.map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 250, maxZoom: 18 });
  };

  /**
   * Paint traveled part up to index i (inclusive) over the full route.
   * Expects i to be an index into the same lonLat array given to setRoute().
   */
  public setProgressIndex = (i: number): void => {
    if (!this.routeAllProj.length) return;
    const n = Math.max(1, Math.min(i + 1, this.routeAllProj.length));
    const traveled = this.routeAllProj.slice(0, n);

    this.routeDoneSource.clear();
    if (traveled.length >= 2) {
      this.routeDoneSource.addFeature(new Feature(new LineString(traveled)));
    }
  };

  public setMarkerPosition = (lat: number, lon: number, pan = false): void => {
    this.ensureMarker();
    const coords = fromLonLat([lon, lat]);
    this.markerFeature!.getGeometry()!.setCoordinates(coords);

    if (pan) {
      // smooth center move; or use .setCenter for hard jump
      this.map.getView().animate({ center: coords, duration: 200 });
    }
  };

  private addPosition = (
    lat: number,
    lon: number,
    features: Feature<Point>[],
    source: any
  ): Feature<Point> => {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      throw new Error('Invalid coordinates for position');
    }

    const coord = fromLonLat([lon, lat]);
    const feature = new Feature<Point>({ geometry: new Point(coord) });

    features.push(feature);
    source.addFeature(feature);

    return feature;
  };

  public addParkPosition = (lat: number, lon: number): Feature<Point> => {
    return this.addPosition(lat, lon, this.parkingFeatures, this.parkingSource);
  };

  public addFuelingPosition = (lat: number, lon: number): Feature<Point> => {
    return this.addPosition(lat, lon, this.fuelingFeatures, this.fuelingSource);
  };

  public addDrainingPosition = (lat: number, lon: number): Feature<Point> => {
    return this.addPosition(lat, lon, this.drainingFeatures, this.drainingSource);
  };

  private ensureMarker = (): void => {
    if (this.markerFeature) return;
    this.markerFeature = new Feature(new Point(fromLonLat(this.center ?? [0, 0])));
    this.markerSource.addFeature(this.markerFeature);
  };


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['polygons'] || changes['polylines'] || changes['pointItems'] && this.map) {
      this.rebuildVectorContent();
    }
  }

  public setCenter(lat: number, lng: number) {
    if (lat == 0 || lng == 0)
      return;

    this.map.getView().setCenter(
      (() => {
        const centerLonLat = [lng, lat];
        return centerLonLat ? fromLonLat(centerLonLat) : this.map.getView().getCenter();
      })()
    );
  }

  public setTargetMarkerSrc(iconSrc: string, anchorX: number, anchorY: number) {
    this.markerLayer.setStyle(new Style({
      image: new Icon({ src: iconSrc, anchor: [anchorX, anchorY], scale: 1 })
    }));
    this.markerLayer.setZIndex(2);
  }

  private rebuildVectorContent() {
    this.vectorSource.clear();
    let showGeozoneText = this.profileStoreService.readMapShowGeozoneNames();

    // 1) Add polygons and polylines and pointItems first (so markers render on top)
    for (const poly of this.polygons ?? []) {
      const coords = (poly.coords ?? []).map((x) => [x.longitude, x.latitude]);
      if (coords.length < 3) continue; // need at least 3 points

      // Ensure the ring is closed
      const isClosed =
        coords.length > 0 &&
        coords[0][0] === coords[coords.length - 1][0] &&
        coords[0][1] === coords[coords.length - 1][1];
      const ring = isClosed ? coords : [...coords, coords[0]];

      const polygon = new Polygon([ring.map(c => fromLonLat(c as [number, number]))]);

      const feature = new Feature({ geometry: polygon });
      const fillColor = poly.fillColor ?? 'rgba(0, 128, 255, 0.25)';
      const strokeColor = poly.strokeColor ?? '#0066cc';
      const strokeWidth = poly.strokeWidth ?? 2;

      feature.setStyle(
        new Style({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
          text: new Text({
            font: '12px Calibri,sans-serif',
            overflow: true,
            text: showGeozoneText ? poly.name : "",
            offsetY: 0,
            offsetX: 0,
          }),
        })
      );

      this.vectorSource.addFeature(feature);
    }

    for (const line of this.polylines ?? []) {
      const coords = (line.coords ?? []).map((x) => [x.longitude, x.latitude]);
      if (coords.length < 2) continue; // need at least 2 points for a line

      const polyline = new LineString(
        coords.map((c) => fromLonLat(c as [number, number]))
      );

      const feature = new Feature({ geometry: polyline });
      const strokeColor = line.strokeColor ?? '#ff0000';
      const strokeWidth = line.strokeWidth ?? 2;
      feature.setStyle(
        new Style({
          stroke: new Stroke({
            color: strokeColor,
            width: strokeWidth,
          }),
          text: new Text({
            font: '12px Calibri,sans-serif',
            overflow: true,
            text: showGeozoneText ? line.name : "",
            offsetY: 0,
            offsetX: 0,
          }),
        })
      );

      this.vectorSource.addFeature(feature);
    }

    for (const point of this.pointItems ?? []) {
      const centerLonLat = [point.coord.longitude, point.coord.latitude];
      const radiusMeters = point.radius ?? 100;
      const color = point.color ?? '#00cc00';

      const circlePolygon = this.createGeodesicCircle(centerLonLat, radiusMeters, 30);

      const circleFeature = new Feature({ geometry: circlePolygon });
      circleFeature.setStyle(
        new Style({
          fill: new Fill({ color: 'rgba(0, 204, 0, 0.15)' }),
          stroke: new Stroke({ color, width: 2 }),
          text: new Text({
            font: '12px Calibri,sans-serif',
            overflow: true,
            text: showGeozoneText ? point.name : "",
            offsetY: 5,
            offsetX: 5,
          }),
        }),
      );

      this.vectorSource.addFeature(circleFeature);
    }

    // 2) Add markers
    this.addMarkers(this.markers);
    // const view = this.map.getView();
    // if (view) {
    //   if (this.center) {
    //     view.setCenter(fromLonLat(this.center));
    //   } else {
    //     const extent = this.vectorSource.getExtent();
    //     // getExtent() returns [Infinity, …] if empty; guard by checking features
    //     if (this.vectorSource.getFeatures().length > 0) {
    //       view.fit(extent, { padding: [24, 24, 24, 24], duration: 200 });
    //     } else {
    //       view.setCenter(fromLonLat([0, 0]));
    //       view.setZoom(this.zoom ?? 2);
    //     }
    //   }
    // }
  }

  public addMarkers(markers: IMarkerItem[]) {
    let showObjectsText = this.profileStoreService.readMapShowGeozoneNames();

    for (const marker of markers ?? []) {
      if (marker.lng == 0 || marker.lat == 0) continue;
      const feature = new Feature({
        geometry: new Point(fromLonLat([marker.lng, marker.lat])),
      });

      feature.setStyle(
        new Style({
          image: new Icon({
            src: marker.iconSrc,
            anchor: [0.5, 0.5],
            scale: 0.85,
            rotation: marker.rotate && marker.heading ? (marker.heading * Math.PI) / 180 : 0
          }),
          text: new Text({
            font: '12px Calibri,sans-serif',
            overflow: true,
            text: showObjectsText ? marker.name : "",
            offsetY: -20,
            offsetX: 35,
          }),
        })
      );
      this.vectorSource.addFeature(feature);
    }
  }

  private createGeodesicCircle(centerLonLat, radiusMeters, numPoints = 30) {
    const [lon, lat] = centerLonLat;
    const coords = [];
    const step = 360 / numPoints;

    for (let i = 0; i < numPoints; i++) {
      const angle = i * step;
      const offset = this.destinationPoint(lon, lat, radiusMeters, angle);
      coords.push(fromLonLat(offset));
    }

    // Close the polygon
    coords.push(coords[0]);
    return new Polygon([coords]);
  }

  // Calculate destination point given distance (m) and bearing (°)
  private destinationPoint(lon, lat, distance, bearing) {
    const R = 6378137; // Earth radius (m)
    const δ = distance / R;
    const θ = (bearing * Math.PI) / 180;
    const φ1 = (lat * Math.PI) / 180;
    const λ1 = (lon * Math.PI) / 180;

    const φ2 = Math.asin(
      Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ)
    );
    const λ2 =
      λ1 +
      Math.atan2(
        Math.sin(θ) * Math.sin(δ) * Math.cos(φ1),
        Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2)
      );

    return [(λ2 * 180) / Math.PI, (φ2 * 180) / Math.PI];
  }
}
